import { useState, useEffect } from "react";
import { X, Car, Loader2, Building, FileText } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Card, CardContent } from "../../../../components/ui/card";
import { useAuth } from "../../../../context/AuthContext";
import Swal from "sweetalert2";
import { Vehiculo, obtenerVehiculoDetalle, ObtenerVehiculoDetalleRequest } from "../../../../api/vehiculoService";

interface VehicleDetailModalProps {
  isOpen: boolean;
  vehiculo: Vehiculo;
  onClose: () => void;
}

export default function VehicleDetailModal({ isOpen, vehiculo, onClose }: VehicleDetailModalProps) {
  const { session } = useAuth();
  const unidadId = session?.unidad?.id || "";
  
  const [vehiculoDetalle, setVehiculoDetalle] = useState<Vehiculo | null>(null);
  const [isLoadingDetalle, setIsLoadingDetalle] = useState(false);

  useEffect(() => {
    if (isOpen && vehiculo?.veh_codigo && unidadId) {
      loadDetalle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, vehiculo?.veh_codigo, unidadId]);

  const loadDetalle = async () => {
    if (!vehiculo?.veh_codigo || !unidadId) return;
    
    setIsLoadingDetalle(true);
    try {
      const payload: ObtenerVehiculoDetalleRequest = {
        codigo: String(vehiculo.veh_codigo),
        "unidad residencial codigo": unidadId,
        "apartamento codigo": "",
        "numero placa": "",
      };

      const response = await obtenerVehiculoDetalle(payload);
      
      if (response.code === "1" && response.object_ && response.object_.length > 0) {
        setVehiculoDetalle(response.object_[0]);
      } else {
        setVehiculoDetalle(vehiculo);
      }
    } catch (error) {
      console.error("Error al cargar detalle:", error);
      setVehiculoDetalle(vehiculo);
    } finally {
      setIsLoadingDetalle(false);
    }
  };

  const getImageUrl = (rutaFoto?: string) => {
    if (!rutaFoto || rutaFoto === "no_hay_imagen.png") {
      return null;
    }
    if (rutaFoto.startsWith('http://') || rutaFoto.startsWith('https://')) {
      return rutaFoto;
    }
    const apiBaseUrl = process.env.REACT_APP_API_PROD || 'https://api.easyph.com.co/api/';
    const baseUrl = apiBaseUrl.replace(/\/api\/?$/, '');
    return `${baseUrl}/${rutaFoto}`;
  };

  if (!isOpen) return null;

  const vehiculoMostrar = vehiculoDetalle || vehiculo;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[95vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex items-center justify-between z-10">
          <h3 className="text-lg sm:text-xl font-bold text-military-600 flex items-center gap-2">
            <Car className="h-5 w-5 sm:h-6 sm:w-6" />
            <span className="truncate">Detalle del Vehículo</span>
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition text-2xl font-bold leading-none"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>
        
        <div className="p-4 sm:p-6 space-y-4 sm:space-y-5">
          {isLoadingDetalle ? (
            <div className="flex flex-col items-center justify-center p-8 sm:p-12">
              <Loader2 className="h-12 w-12 animate-spin text-military-500 mb-4" />
              <p className="text-gray-600 text-sm sm:text-base">Cargando información...</p>
            </div>
          ) : (
            <>
              <div className="flex justify-center">
                {(() => {
                  const imageUrl = getImageUrl(vehiculoMostrar.veh_foto);
                  
                  return imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={vehiculoMostrar.veh_numero_placa}
                      className="w-full max-w-md rounded-xl object-cover border-4 border-military-500 shadow-lg"
                    />
                  ) : (
                    <div className="w-full max-w-md h-48 rounded-xl bg-gray-200 flex items-center justify-center border-4 border-military-500 shadow-lg">
                      <Car className="h-24 w-24 text-gray-400" />
                    </div>
                  );
                })()}
              </div>
              
              <div className="grid grid-cols-1 gap-3 sm:gap-4">
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200">
                  <p className="font-semibold text-xs sm:text-sm text-gray-700 mb-2 flex items-center gap-2">
                    <Building className="h-4 w-4" />
                    Torre
                  </p>
                  <p className="text-sm sm:text-base text-gray-900 break-words">
                    {vehiculoMostrar.veh_torre_nombre || "No especificada"}
                  </p>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200">
                  <p className="font-semibold text-xs sm:text-sm text-gray-700 mb-2 flex items-center gap-2">
                    <Building className="h-4 w-4" />
                    Apartamento
                  </p>
                  <p className="text-sm sm:text-base text-gray-900 break-words">
                    {vehiculoMostrar.veh_apartamento_nombre || "No especificado"}
                  </p>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200">
                  <p className="font-semibold text-xs sm:text-sm text-gray-700 mb-2 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Número de Placa
                  </p>
                  <p className="text-sm sm:text-base text-gray-900 break-words">
                    {vehiculoMostrar.veh_numero_placa || "No especificada"}
                  </p>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200">
                  <p className="font-semibold text-xs sm:text-sm text-gray-700 mb-2">Ciudad de la Placa</p>
                  <p className="text-sm sm:text-base text-gray-900 break-words">
                    {vehiculoMostrar.veh_nombre_ciudad || "No especificada"}
                  </p>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200">
                  <p className="font-semibold text-xs sm:text-sm text-gray-700 mb-2 flex items-center gap-2">
                    <Car className="h-4 w-4" />
                    Tipo de Vehículo
                  </p>
                  <p className="text-sm sm:text-base text-gray-900 break-words">
                    {vehiculoMostrar.veh_tipo_nombre || "No especificado"}
                  </p>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200">
                  <p className="font-semibold text-xs sm:text-sm text-gray-700 mb-2">Marca del Vehículo</p>
                  <p className="text-sm sm:text-base text-gray-900 break-words">
                    {vehiculoMostrar.veh_marca_nombre || "No especificada"}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
        
        <div className="border-t border-gray-200 p-4 sm:p-6 flex justify-end">
          <Button
            onClick={onClose}
            variant="outline"
            className="w-full sm:w-auto text-sm sm:text-base"
            disabled={isLoadingDetalle}
          >
            Salir
          </Button>
        </div>
      </div>
    </div>
  );
}

